import type { StagedFile } from "@/api/datasetUpload";
import type { PageQuery, PageResp } from "@/api/page";
import type { ScannedFile } from "./folderScan";

// 재개(resume) 경로의 staging 실측(probe) 수집과 path+size diff.
//
// Owns:
// - "이미 올라간 파일" 판정 정책: relative_path 일치 + size_bytes 일치. 서버 size 가 null 이면
//   불일치로 본다. 확신 없이 건너뛰는 것보다 다시 올리는 것이 안전하다. one-shot PUT 은 덮어쓰기라
//   다시 올려도 문제가 없다.
// - probe 전체 페이지 순회. 서버가 페이지마다 prefix 를 다시 나열(O(P·N))하므로 재개 시 1회만 호출한다.
//
// Does not own:
// - diff 결과의 실행(건너뜀 표시, orphan 삭제, 업로드). UploadEngine 이 소유한다.

export type StagedDiff = {
  // path+size 가 완전히 일치하는 파일. 업로드를 생략한다.
  skipped: ScannedFile[];
  // staging 에 없거나 크기가 다른 파일. PUT(덮어쓰기 포함) 대상이다.
  toUpload: ScannedFile[];
  // 크기 불일치로 toUpload 에 포함된 부분집합. UI 경고 문구에 쓴다.
  mismatched: ScannedFile[];
  // staging 에만 있고 로컬 폴더에는 없는 파일. prune(삭제) 또는 검토 대상이다.
  orphans: StagedFile[];
};

export function diffStagedFiles(
  scanned: readonly ScannedFile[],
  staged: readonly StagedFile[],
): StagedDiff {
  const stagedByPath = new Map(staged.map((item) => [item.relative_path, item]));
  const skipped: ScannedFile[] = [];
  const toUpload: ScannedFile[] = [];
  const mismatched: ScannedFile[] = [];
  for (const file of scanned) {
    const stagedFile = stagedByPath.get(file.relativePath);
    if (stagedFile === undefined) {
      toUpload.push(file);
      continue;
    }
    stagedByPath.delete(file.relativePath);
    if (stagedFile.size_bytes === file.sizeBytes) {
      skipped.push(file);
    } else {
      mismatched.push(file);
      toUpload.push(file);
    }
  }
  return { skipped, toUpload, mismatched, orphans: [...stagedByPath.values()] };
}

export type ListStagedFilesFn = (query: PageQuery) => Promise<PageResp<StagedFile>>;

// staged 파일의 모든 페이지를 수집한다. total 이 페이지 사이에 바뀔 수 있으므로(실측 나열)
// 마지막 페이지는 반환 개수 < size 로 판정한다.
export async function fetchAllStagedFiles(
  listStagedFiles: ListStagedFilesFn,
  pageSize: number,
): Promise<StagedFile[]> {
  const all: StagedFile[] = [];
  for (let page = 1; ; page += 1) {
    const resp = await listStagedFiles({ page, size: pageSize });
    all.push(...resp.items);
    if (resp.items.length < pageSize) {
      return all;
    }
  }
}
